import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'
import type { ListingRemakeSlot } from '@/lib/api/listing-builder.types'

interface RemakeSlotCardProps {
  slot: ListingRemakeSlot
}

const STATUS_VARIANT: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  generating: 'warning',
  completed: 'success',
  failed: 'error',
}

/** Read an image URL from a generation object (tolerant of field naming). */
function generationUrl(gen: Record<string, unknown> | undefined): string | null {
  if (!gen) return null
  const candidate = gen.asset_url ?? gen.url ?? gen.image_url
  return typeof candidate === 'string' ? candidate : null
}

/** Read-only display of a single remake image slot. */
export function RemakeSlotCard({ slot }: RemakeSlotCardProps) {
  const { t } = useTranslation()
  const selected =
    slot.generations.find((g) => g.id === slot.selected_generation_id) ?? slot.generations[0]
  const imageUrl = generationUrl(selected)

  return (
    <div
      id={`${EID['listing-builder-remake-slot-card']}-${slot.slot_number}`}
      className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {slot.slot_purpose}
        </span>
        <Badge variant={STATUS_VARIANT[slot.status] ?? 'default'}>
          {t(`listingBuilder.remake.slotStatus.${slot.status}`)}
        </Badge>
      </div>

      <div className="aspect-square rounded-lg bg-neutral-100 dark:bg-neutral-800 overflow-hidden flex items-center justify-center">
        {imageUrl ? (
          <img src={imageUrl} alt={slot.slot_purpose} className="w-full h-full object-contain" />
        ) : (
          <span className="text-xs text-neutral-400">{t('listingBuilder.remake.noImage')}</span>
        )}
      </div>

      {slot.overlay_text && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{slot.overlay_text}</p>
      )}
      {slot.status === 'failed' && slot.error_message && (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {slot.error_message}
        </p>
      )}
    </div>
  )
}
